import React, { useState, useRef, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import { uploadedMarkers } from 'constants.js'
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate'
import PaneHeader from '../panes/PaneHeader'
import UserMarkerfileList from './UserMarkerfileList'
import ErrorForm from '../common/ErrorForm'
import UploadMarkerfileTooltip from './UploadMarkerfileTooltip'

const UserMarkerfileListContainer = ({ userMarkerfileList, selectionStore, uiStore, t }) => {
  const initialMode = (selectionStore && selectionStore.data && selectionStore.data.initialMode) || (userMarkerfileList && userMarkerfileList.initialMode) || 'list'
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState(initialMode) // 'list' or 'upload'
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFileObj, setSelectedFileObj] = useState(null)
  const [fileAnalysis, setFileAnalysis] = useState({ metaStr: '', hasCoords: true, rowCount: null })
  const [showHelpFormat, setShowHelpFormat] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null)

  const allFiles = userMarkerfileList.markerfiles || []

  useEffect(() => {
    const map = uiStore && uiStore.map && uiStore.map.leafletElement
    const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
    const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
    if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
      uiStore.suppressMapBoundsTemporarily(500)
    }
    const timer = setTimeout(() => {
      if (map && center && zoom != null) {
        map.setView(center, zoom, { animate: false })
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [])

  // If there are no files at all initially, start in 'upload' mode if appropriate,
  // or if opened from a specific upload trigger
  useEffect(() => {
    const mode = (selectionStore && selectionStore.data && selectionStore.data.initialMode) || (userMarkerfileList && userMarkerfileList.initialMode)
    if (mode === 'upload') {
      setViewMode('upload')
    }
    if (userMarkerfileList.chosenFile) {
      handleFileSelected(userMarkerfileList.chosenFile)
      setViewMode('upload')
    }
  }, [selectionStore && selectionStore.data && selectionStore.data.initialMode, userMarkerfileList && userMarkerfileList.initialMode, userMarkerfileList.chosenFile])

  const handleClose = () => {
    selectionStore.clear()
  }

  const handleFileSelected = (file) => {
    if (!file) return
    setSelectedFileObj(file)
    setViewMode('upload')

    // Format size and type
    const sizeInKB = file.size ? file.size / 1024 : 0
    const sizeStr = sizeInKB > 1024
      ? `${(sizeInKB / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(sizeInKB))} KB`
    const ext = ((file.name && file.name.split('.').pop()) || 'CSV').toUpperCase()
    const metaStr = `${sizeStr} · ${ext}`

    // Parse CSV to detect headers & count exact data rows
    if (file.name && file.name.toLowerCase().endsWith('.csv')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const text = e.target.result || ''
          const lines = text.split(/\r\n|\n/).filter(line => line.trim().length > 0)
          const header = (lines[0] || '').toLowerCase()
          const hasLat = header.includes('lat') || header.includes('latitude') || header.includes('y')
          const hasLng = header.includes('lon') || header.includes('lng') || header.includes('longitude') || header.includes('x')
          const rowCount = Math.max(0, lines.length - 1)
          setFileAnalysis({
            metaStr,
            hasCoords: hasLat && hasLng,
            rowCount: rowCount.toLocaleString()
          })
        } catch (err) {
          setFileAnalysis({ metaStr, hasCoords: true, rowCount: null })
        }
      }
      reader.onerror = () => setFileAnalysis({ metaStr, hasCoords: true, rowCount: null })
      reader.readAsText(file)
    } else if (file.name && (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls'))) {
      // Parse XLSX to count exact data rows
      XlsxPopulate.fromDataAsync(file).then(workbook => {
        try {
          const sheet = workbook.sheet(0)
          const usedRange = sheet ? sheet.usedRange() : null
          if (usedRange) {
            const rowCount = Math.max(0, usedRange.endCell().rowNumber() - 1)
            setFileAnalysis({
              metaStr,
              hasCoords: true,
              rowCount: rowCount.toLocaleString()
            })
          } else {
            setFileAnalysis({ metaStr, hasCoords: true, rowCount: '0' })
          }
        } catch (err) {
          setFileAnalysis({ metaStr, hasCoords: true, rowCount: null })
        }
      }).catch(() => {
        setFileAnalysis({ metaStr, hasCoords: true, rowCount: null })
      })
    } else {
      setFileAnalysis({ metaStr, hasCoords: true, rowCount: null })
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const files = e.dataTransfer && e.dataTransfer.files
    if (files && files[0]) {
      handleFileSelected(files[0])
    }
  }

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0]
    if (file) {
      handleFileSelected(file)
    }
  }

  const handleUploadMarkers = async () => {
    if (!selectedFileObj) return
    setIsUploading(true)
    userMarkerfileList.chosenFile = selectedFileObj

    if (userMarkerfileList.validate(selectedFileObj)) {
      try {
        await uiStore.uploadUserMarkerfile(userMarkerfileList)
        setSelectedFileObj(null)
        setViewMode('list')
      } catch (err) {
        console.error('Error uploading marker file:', err)
      } finally {
        setIsUploading(false)
      }
    } else {
      setIsUploading(false)
    }
  }

  const handleCancelUpload = () => {
    setSelectedFileObj(null)
    userMarkerfileList.chosenFile = null
    userMarkerfileList.invalid.clear()
    setViewMode('list')
  }

  const filteredFiles = allFiles.filter(file => {
    if (!searchTerm || !searchTerm.trim()) return true
    const fileName = file.name || ''
    return fileName.toLowerCase().includes(searchTerm.toLowerCase().trim())
  })

  const activeFile = selectedFileObj || userMarkerfileList.chosenFile

  // ==========================================
  // VIEW 1 & 2: UPLOAD FLOW (GU-14 & GU-15)
  // ==========================================
  if (viewMode === 'upload') {
    return (
      <div className='marker-file-pane'>
        {/* Hidden file input */}
        <input
          type='file'
          ref={fileInputRef}
          accept='.csv,.xlsx'
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />

        {/* Pane Header */}
        <PaneHeader
          icon={uploadedMarkers}
          title={t('GLOBAL.UPLOAD_MARKER_FILE_TITLE', { defaultValue: 'Upload marker file' })}
          onClose={handleClose}
        />

        <p className='marker-file-pane__subtitle'>
          {t('GLOBAL.UPLOAD_MARKER_FILE_SUBTITLE', { defaultValue: 'Upload a CSV or XLSX file containing marker locations.' })}
        </p>

        <div className='marker-upload'>
          {/* GU-14: No file selected yet */}
          {!activeFile
            ? (
              <>
                {/* Dropzone */}
                <div
                  className={`marker-upload__dropzone ${isDragging ? 'is-dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleChooseFileClick}
                >
                  <div className='marker-upload__dropzone-icon'>
                    <svg width='30' height='30' viewBox='0 0 24 24' fill='none' stroke='#5EB1BC' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                      <polyline points='17 8 12 3 7 8' />
                      <line x1='12' y1='3' x2='12' y2='15' />
                    </svg>
                  </div>
                  <span className='marker-upload__dropzone-title'>
                    {t('GLOBAL.DROP_MARKER_FILE', { defaultValue: 'Drop a marker file here' })}
                  </span>
                  <span className='marker-upload__dropzone-subtitle'>
                    {t('GLOBAL.OR_CHOOSE_FILE', { defaultValue: 'or choose a file from your computer' })}
                  </span>
                </div>

                {/* Supported formats info */}
                <div className='marker-upload__info-card'>
                  <div className='marker-upload__info-label'>
                    {t('GLOBAL.SUPPORTED_FORMATS', { defaultValue: 'Supported formats' })}
                  </div>
                  <div className='marker-upload__info-value'>
                    {t('GLOBAL.SUPPORTED_FORMATS_DESC', { defaultValue: 'CSV or XLSX · Maximum file size 20 MB' })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='marker-upload__actions'>
                  <button
                    type='button'
                    className='marker-upload__btn-cancel'
                    onClick={handleCancelUpload}
                  >
                    {t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}
                  </button>
                  <button
                    type='button'
                    className='marker-upload__btn-primary'
                    onClick={handleChooseFileClick}
                  >
                    {t('GLOBAL.CHOOSE_FILE', { defaultValue: 'Choose file' })}
                  </button>
                </div>

                {/* Help link */}
                <div className='marker-upload__help-link'>
                  <button
                    type='button'
                    className='marker-upload__help-btn'
                    onClick={() => setShowHelpFormat(!showHelpFormat)}
                  >
                    {t('GLOBAL.NEED_HELP_FORMATTING', { defaultValue: 'Need help formatting your file?' })}
                  </button>
                </div>

                {showHelpFormat && (
                  <div className='marker-upload__help-modal'>
                    <UploadMarkerfileTooltip intro={t('MARKERFILE_TOOLTIP', { defaultValue: 'File should contain columns' })} />
                  </div>
                )}
              </>
              )
            : (
              /* GU-15: File selected */
              <>
                {/* File Card */}
                <div className='marker-upload__file-card'>
                  <div className='marker-upload__file-card-left'>
                    <div className='marker-upload__file-icon-badge'>
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#5EB1BC'
                        strokeWidth='1.75'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                        <polyline points='14 2 14 8 20 8' />
                        <path d='M12 18a2.5 2.5 0 0 0 2.5-2.5c0-1.5-2.5-4-2.5-4s-2.5 2.5-2.5 4a2.5 2.5 0 0 0 2.5 2.5z' />
                      </svg>
                    </div>
                    <div className='marker-upload__file-card-info'>
                      <span className='marker-upload__file-card-name' title={(activeFile && activeFile.name) || 'store-locations.csv'}>
                        {(activeFile && activeFile.name) || 'store-locations.csv'}
                      </span>
                      <span className='marker-upload__file-card-meta'>
                        {fileAnalysis.metaStr || (activeFile && activeFile.size ? `${Math.max(1, Math.round(activeFile.size / 1024))} KB · ${(activeFile.name ? activeFile.name.split('.').pop() : 'CSV').toUpperCase()}` : '248 KB · CSV')}
                      </span>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='marker-upload__file-card-change'
                    onClick={handleChooseFileClick}
                  >
                    {t('GLOBAL.CHANGE', { defaultValue: 'Change' })}
                  </button>
                </div>

                {/* Detection Status Banner */}
                <div className={`marker-upload__status-banner ${!fileAnalysis.hasCoords ? 'has-error' : ''}`}>
                  <div className='marker-upload__status-top'>
                    <div className='marker-upload__status-icon'>
                      {fileAnalysis.hasCoords
                        ? (
                          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#0D9488' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                            <circle cx='12' cy='12' r='10' />
                            <polyline points='9 12 11 14 15 10' />
                          </svg>
                          )
                        : (
                          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#DC2626' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                            <circle cx='12' cy='12' r='10' />
                            <line x1='12' y1='8' x2='12' y2='12' />
                            <line x1='12' y1='16' x2='12.01' y2='16' />
                          </svg>
                          )}
                    </div>
                    <span className='marker-upload__status-text'>
                      {fileAnalysis.hasCoords
                        ? t('GLOBAL.LAT_LNG_DETECTED', { defaultValue: 'Latitude and longitude columns detected' })
                        : t('GLOBAL.NO_LAT_LNG_DETECTED', { defaultValue: 'Could not detect latitude/longitude columns' })}
                    </span>
                  </div>
                  {fileAnalysis.hasCoords && <div className='marker-upload__status-bar' />}
                </div>

                {/* Before upload info card */}
                <div className='marker-upload__before-card'>
                  <span className='marker-upload__before-title'>
                    {t('GLOBAL.BEFORE_UPLOAD', { defaultValue: 'Before upload' })}
                  </span>
                  <span className='marker-upload__before-item'>
                    {fileAnalysis.rowCount !== null && fileAnalysis.rowCount !== undefined
                      ? `Header row found · ${fileAnalysis.rowCount} markers ready`
                      : t('GLOBAL.HEADER_ROW_FOUND', { defaultValue: 'Header row found · Ready to upload' })}
                  </span>
                  <span className='marker-upload__before-subitem'>
                    {t('GLOBAL.INVALID_COORDS_SKIPPED', { defaultValue: 'Invalid coordinates will be skipped.' })}
                  </span>
                </div>

                {/* Upload error if any */}
                {userMarkerfileList.error && (
                  <div className='marker-file-pane__error'>
                    <ErrorForm item={userMarkerfileList} />
                  </div>
                )}
                {userMarkerfileList.invalid.get('file') && (
                  <div className='marker-file-pane__error'>
                    {userMarkerfileList.invalid.get('file')}
                  </div>
                )}

                {/* Action Buttons */}
                <div className='marker-upload__actions'>
                  <button
                    type='button'
                    className='marker-upload__btn-cancel'
                    onClick={handleCancelUpload}
                    disabled={isUploading}
                  >
                    {t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}
                  </button>
                  <button
                    type='button'
                    className='marker-upload__btn-primary'
                    onClick={handleUploadMarkers}
                    disabled={isUploading || !fileAnalysis.hasCoords}
                  >
                    {isUploading
                      ? (
                        <>
                          <div className='marker-file-pane__spinner' style={{ width: 16, height: 16, borderWidth: 2 }} />
                          <span>{t('GLOBAL.UPLOADING', { defaultValue: 'Uploading...' })}</span>
                        </>
                        )
                      : (
                        <span>{t('GLOBAL.UPLOAD_MARKERS', { defaultValue: 'Upload markers' })}</span>
                        )}
                  </button>
                </div>
              </>
              )}
        </div>
      </div>
    )
  }

  // ==========================================
  // VIEW 3: MARKER FILE LIBRARY LIST (GU-10)
  // ==========================================
  return (
    <div className='marker-file-pane'>
      {/* Pane Header */}
      <PaneHeader
        icon={uploadedMarkers}
        title={t('GLOBAL.MY_MARKERS', { defaultValue: 'My Markers' })}
        onClose={handleClose}
      />

      <p className='marker-file-pane__subtitle'>
        {t('GLOBAL.CHOOSE_SAVED_CSV', { defaultValue: 'Choose a saved CSV or spreadsheet' })}
      </p>

      {/* Search Bar */}
      <div className='marker-file-pane__search-wrap'>
        <span className='marker-file-pane__search-icon'>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#94A3B8' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='11' cy='11' r='8' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
        </span>
        <input
          type='text'
          className='marker-file-pane__search-input'
          placeholder={t('GLOBAL.SEARCH_SAVED_FILES', { defaultValue: 'Search saved files' })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type='button'
            className='marker-file-pane__search-clear'
            onClick={() => setSearchTerm('')}
            aria-label='Clear search'
            style={{ width: '24px', height: '24px', minWidth: '24px', maxWidth: '24px', padding: 0 }}
          >
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#94A3B8' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        )}
      </div>

      {/* Section Header */}
      <div className='marker-file-pane__section-header'>
        <span className='marker-file-pane__section-title'>
          {t('GLOBAL.MARKER_FILES', { defaultValue: 'Marker files' })}
        </span>
        <span className='marker-file-pane__files-count'>
          {filteredFiles.length} {filteredFiles.length === 1
            ? t('GLOBAL.FILE', { defaultValue: 'file' })
            : t('GLOBAL.FILES', { defaultValue: 'files' })}
        </span>
      </div>

      {/* File List */}
      <div className='marker-file-pane__list-container'>
        {userMarkerfileList.loading
          ? (
            <div className='marker-file-pane__loading'>
              <div className='marker-file-pane__spinner' />
              <span>{t('GLOBAL.LOADING', { defaultValue: 'Loading files...' })}</span>
            </div>
            )
          : filteredFiles.length === 0
            ? (
              <div className='marker-file-pane__empty'>
                {searchTerm
                  ? (
                    <span>No files found matching "{searchTerm}"</span>
                    )
                  : (
                    <span>{t('GLOBAL.NO_MARKERFILES', { defaultValue: 'No saved marker files' })}</span>
                    )}
              </div>
              )
            : (
              <UserMarkerfileList
                userMarkerfileList={userMarkerfileList}
                markerfiles={filteredFiles}
              />
              )}
      </div>

      {/* Error feedback if any */}
      {userMarkerfileList.error && (
        <div className='marker-file-pane__error'>
          <ErrorForm item={userMarkerfileList} />
        </div>
      )}

      {/* Bottom Upload Button */}
      <div className='marker-file-pane__footer'>
        <button
          type='button'
          className='marker-file-pane__upload-btn'
          onClick={() => {
            setSelectedFileObj(null)
            setViewMode('upload')
          }}
          disabled={userMarkerfileList.loading}
        >
          <span className='marker-file-pane__upload-icon'>
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#0D9488' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
              <polyline points='17 8 12 3 7 8' />
              <line x1='12' y1='3' x2='12' y2='15' />
            </svg>
          </span>
          <span>{t('GLOBAL.UPLOAD_MARKER_FILE_IN_MY_DATA', { defaultValue: 'Upload marker file in My Data' })}</span>
        </button>
      </div>
    </div>
  )
}

export default withTranslation()(
  inject('selectionStore', 'uiStore')(observer(UserMarkerfileListContainer))
)
